import type {
	TContentPart,
	TPaulInputSubmission,
} from "../../types/agent";

import { parsePaulInputSpec } from "../../utils/paulInput";

import PaulInputPrompt from "./PaulInputPrompt";
import CSVRenderer from "./renderers/CSVRenderer";
import HeaderRenderer from "./renderers/HeaderRenderer";
import ImageRenderer from "./renderers/ImageRenderer";
import LargeParagraphRenderer from "./renderers/LargeParagraphRenderer";
import MarkdownRenderer from "./renderers/MarkdownRenderer";
import ParagraphRenderer from "./renderers/ParagraphRenderer";

type TPaulMessageContentProps = {
	parts: Array<TContentPart>;
	inputPresentation?: "inline" | "conversation";
	freeformInputPresentation?: "inline" | "question";
	pendingInputQuestionIds?: Set<string>;
	inputAnswersByQuestionId?: Map<string, string>;
	inputsDisabled?: boolean;
	readOnly?: boolean;
	onSubmitInput?: (
		submission: TPaulInputSubmission
	) => Promise<void> | void;
};

const PaulMessageContent = ({
	parts,
	inputPresentation = "inline",
	freeformInputPresentation = "inline",
	pendingInputQuestionIds = new Set(),
	inputAnswersByQuestionId = new Map(),
	inputsDisabled = false,
	readOnly = false,
	onSubmitInput,
}: TPaulMessageContentProps) => {
	return (
		<>
			{parts.map((part, index) => {
				const {
					msg_type: messageType,
					content,
					blob_link: blobLink,
					description,
					level,
				} = part;

				switch (messageType) {
					case "input":
						if (
							freeformInputPresentation === "question" &&
							parsePaulInputSpec(part)?.inputKind ===
								"freeform"
						) {
							const answeredValue = part.question_id
								? inputAnswersByQuestionId.get(
										part.question_id
									)
								: undefined;
							return (
								<div
									key={part.question_id ?? index}
									className="ledger-paul-freeform-question"
								>
									<ParagraphRenderer content={content} />
									{answeredValue !== undefined ? (
										<p className="ledger-paul-freeform-answer">
											Answer:{" "}
											{answeredValue || "No response"}
										</p>
									) : null}
								</div>
							);
						}
						if (inputPresentation === "conversation") {
							const spec = parsePaulInputSpec(part);
							const minimum =
								spec?.inputKind === "freeform"
									? spec.constraints.min
									: undefined;
							const maximum =
								spec?.inputKind === "freeform"
									? spec.constraints.max
									: undefined;
							const answeredValue = part.question_id
								? inputAnswersByQuestionId.get(
										part.question_id
									)
								: undefined;
							return (
								<div
									key={part.question_id ?? index}
									className="ledger-paul-question-summary"
								>
									<ParagraphRenderer content={content} />
									{minimum !== undefined ||
									maximum !== undefined ? (
										<div className="ledger-paul-question-guidance">
											{minimum !== undefined ? (
												<span>
													Min <strong>{minimum}</strong>
												</span>
											) : null}
											{maximum !== undefined ? (
												<span>
													Max <strong>{maximum}</strong>
												</span>
											) : null}
										</div>
									) : null}
									{answeredValue !== undefined ? (
										<div className="ledger-paul-question-answer">
											<span>Answer</span>
											<strong>
												{answeredValue || "No response"}
											</strong>
										</div>
									) : null}
								</div>
							);
						}
						return (
							<PaulInputPrompt
								key={part.question_id ?? index}
								part={part}
								active={Boolean(
									part.question_id &&
										pendingInputQuestionIds.has(
											part.question_id
										)
								)}
								answeredValue={
									part.question_id
										? inputAnswersByQuestionId.get(
												part.question_id
											)
										: undefined
								}
								disabled={inputsDisabled}
								readOnly={readOnly}
								onSubmit={onSubmitInput}
							/>
						);
					case "input_response":
						// Rendered on its picker via answeredValue, not as its own part.
						return null;
					case "image":
						if (!blobLink || blobLink.trim() === "") {
							return null;
						}
						return (
							<ImageRenderer
								key={index}
								src={blobLink}
								alt={description}
							/>
						);
					case "header":
						return (
							<HeaderRenderer
								key={index}
								content={content}
								level={level}
							/>
						);
					case "paragraph":
						return (
							<ParagraphRenderer
								key={index}
								content={content}
							/>
						);
					case "dataframe":
						return (
							<CSVRenderer
								key={index}
								blobLink={blobLink ?? ""}
								description={description}
							/>
						);
					case "large_paragraph":
						return (
							<LargeParagraphRenderer
								key={index}
								blobLink={blobLink}
								description={description}
								content={content}
							/>
						);
					default:
						return (
							<MarkdownRenderer
								key={index}
								content={content}
							/>
						);
				}
			})}
		</>
	);
};

export default PaulMessageContent;
